import { Input, InputProps } from "@chakra-ui/react";
import { ChangeEvent, useState } from "react";

import { $value, $ok, $valueorundefine } from "foundation-ts/commons";
import { $ftrim } from "foundation-ts/strings";
import { TSDate } from "foundation-ts/tsdate";
import { TSDateForm } from "foundation-ts/tsdatecomp";
import { Ascending, Descending, Nullable } from "foundation-ts/types";

import { $g1Color } from "@/theme/G1Style";
import { Component } from "@utils/TypesAndConstants";

interface G1DateInputProps extends InputProps {
    component: Component;
    disabled?: boolean;
    dateValue?:Nullable<TSDate>;
    mindate?: Nullable<TSDate>;
    maxdate?: Nullable<TSDate>;
    onDateChange?: (newDate: Nullable<TSDate>) => TSDate|undefined; // changes onBlur and onChange if the date is valid
    registerInput?: any;
}

export const G1DateInput = (props: G1DateInputProps) => {
    const { component, dateValue, onDateChange, registerInput, ...rest } = props ;
    let minDate = props.mindate ;
    let maxDate = props.maxdate ; 
    if ($ok(maxDate) && minDate?.compare(maxDate!) == Descending) {
        const d = minDate ;
        minDate = maxDate ;
        maxDate = d ;
    }
    function _restraintDate(d:Nullable<TSDate>):TSDate {
        d = $value(d, TSDate.zulu().dateWithoutTime()) ; // QUESTION: we may need to change zulu here
        if (minDate?.compare(d) == Descending) { d = minDate! ;}
        if (maxDate?.compare(d) == Ascending) { d = maxDate! ;} 
        return d ;   
    }

    function _localValidateDate(d:Nullable<TSDate>, restrain:boolean):[TSDate|null, string] {
        if (!$ok(d)) { 
            return [null, 'Malformed date'] ; 
        }
        if (restrain) {
            return [_restraintDate(d), ''] ;
        }
        if (minDate?.compare(d) == Descending) {
            return [null, 'Date before minimal date'] ; 
        }
        if (maxDate?.compare(d) == Ascending) {
            return [null, 'Date after maximal date'] ; 
        }
        return [d!,''] ;
    }
    
    function _localValidateDateString(s:string, restrain:boolean = false):[TSDate|null, string] {
        s = $ftrim(s) ; 
        if (!s.length) { 
            return [null, ''] ; // no real error if nothing is typed in the field 
        }
        return _localValidateDate(TSDate.fromDateString(s, TSDateForm.Computer), restrain) ;
    }


    const [isTyping, setIsTyping] = useState(false);
    const [selectedDate, setSelectedDate] = useState(_restraintDate(dateValue).toString("%Y-%m-%d"));
    const [error, setError] = useState<string>('');

    const handleKeyDown = (_event: ChangeEvent<HTMLInputElement>): void => setIsTyping(true) ;
    const handleBlur = (event: ChangeEvent<HTMLInputElement>): void => {
        setIsTyping(false);
        let [date, error] = _localValidateDateString(event.target.value, true) ;
        const newDate = onDateChange?.(date) ;
        if (newDate !== undefined && typeof newDate !== 'undefined') {
            [date, error] = _localValidateDate(newDate, true) ;
            setSelectedDate(newDate.toString("%Y-%m-%d"))
        }
        setError(error) ;
    };
    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const s = $ftrim(event.target.value) ;
        if (s.length === 10) {
            let [date, error] = _localValidateDateString(s) ;
            if ($ok(date) && !error.length) { 
                const newDate = onDateChange?.(date) ;
                if (newDate !== undefined && typeof newDate !== 'undefined') {
                    [date, error] = _localValidateDate(newDate, true) ;
                    setSelectedDate(newDate.toString("%Y-%m-%d"))
                }
                else {
                    setSelectedDate(date!.toString("%Y-%m-%d"))
                }
            }
            else {
                setSelectedDate(s) ;
            }
            setIsTyping(false);
            setError(error) ;
        }
        else {
            setSelectedDate(s) ;
            setIsTyping(true);
            setError('') ; // no error during typing
        }
    };

    return <Input
        type='date'
        value={selectedDate}
        bg={$g1Color(`${component}.field.bg`)}
        color={$g1Color(`${component}.field.write`)}
        borderColor={$g1Color(`${component}.field.border`)}
        _hover={{ borderColor: $g1Color(`${component}.field.hover.border`) }}
        _focus={{ borderColor: $g1Color(`${component}.field.select.border`) }}
        _placeholder={{ color: $g1Color(`${component}.field.placeholder`) }}
        min={$valueorundefine(minDate?.toString("%Y-%m-%d"))}
        max={$valueorundefine(maxDate?.toString("%Y-%m-%d"))}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        isInvalid={error.length > 0 && !isTyping}
        {...registerInput}
        {...rest}
    />;
}

